import React, { useEffect, useState } from "react";
import api from "../../services/api";

const cardStyle = {
  background: "#fff",
  border: "1px solid #e5e7eb",
  borderRadius: 16,
  padding: 16,
  boxShadow: "0 10px 30px rgba(15, 23, 42, 0.06)"
};

const green = "#16a34a";
const red = "#dc2626";
const orange = "#f59e0b";
const blue = "#2563eb";
const purple = "#7c3aed";
const dark = "#0f172a";

const number = value => Number(value || 0);

const money = value =>
  new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL"
  }).format(Number(value || 0));

const percent = value => `${Number(value || 0).toFixed(2)}%`;

const dateTime = value => {
  if (!value) return "-";

  try {
    return new Date(value).toLocaleString("pt-BR");
  } catch {
    return value;
  }
};

const RecoveryDashboard = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({
    summary: {},
    sellers: [],
    latest: [],
    recoveredLatest: []
  });

  const summary = data.summary || {};
  const sellers = data.sellers || [];
  const latest = data.latest || [];
  const recoveredLatest = data.recoveredLatest || [];

  const loadData = async () => {
    try {
      setLoading(true);
      const response = await api.get("/dashboard/recovery");
      setData(response.data || {});
    } catch (err) {
      console.error("Erro ao carregar painel de recuperação:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const cards = [
    { label: "Total em recuperação", value: number(summary.totalRecovery), color: blue },
    { label: "Recuperação 1/3", value: number(summary.stageOne), color: dark },
    { label: "Recuperação 2/3", value: number(summary.stageTwo), color: purple },
    { label: "Recuperação 3/3", value: number(summary.stageThree), color: orange },
    { label: "Vencem hoje", value: number(summary.todayRecovery), color: orange },
    { label: "Atrasados", value: number(summary.overdueRecovery), color: red },
    { label: "Vendas recuperadas", value: number(summary.recoveredSales), color: green },
    { label: "Valor recuperado", value: money(summary.recoveredAmount), color: green },
    { label: "Taxa de recuperação", value: percent(summary.recoveryRate), color: purple },
    { label: "Pendentes", value: number(summary.pendingRecovery), color: blue },
    { label: "Futuros", value: number(summary.futureRecovery), color: green }
  ];

  return (
    <div style={{ marginBottom: 24 }}>
      <div
        style={{
          background: "linear-gradient(135deg, #111827 0%, #991b1b 55%, #ef4444 100%)",
          color: "#fff",
          borderRadius: 18,
          padding: 22,
          marginBottom: 16,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 16
        }}
      >
        <div>
          <div style={{ fontSize: 12, opacity: 0.85, marginBottom: 6 }}>
            Recuperação Comercial
          </div>
          <div style={{ fontSize: 26, fontWeight: 900 }}>
            Painel da Régua de Follow-up
          </div>
          <div style={{ fontSize: 13, opacity: 0.9, marginTop: 6 }}>
            Acompanhe D+1, D+3, D+7, atrasos, vendas recuperadas e valor trazido pela régua.
          </div>
        </div>

        <button
          type="button"
          onClick={loadData}
          disabled={loading}
          style={{
            border: 0,
            borderRadius: 10,
            padding: "10px 16px",
            background: "#fff",
            color: "#b91c1c",
            fontWeight: 800,
            cursor: loading ? "not-allowed" : "pointer"
          }}
        >
          {loading ? "Atualizando..." : "Atualizar"}
        </button>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
          gap: 12,
          marginBottom: 16
        }}
      >
        {cards.map(card => (
          <div key={card.label} style={cardStyle}>
            <div style={{ fontSize: 12, color: "#64748b", marginBottom: 8 }}>
              {card.label}
            </div>
            <div style={{ fontSize: 26, fontWeight: 900, color: card.color }}>
              {card.value}
            </div>
          </div>
        ))}
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1.2fr 1fr",
          gap: 16,
          marginBottom: 16
        }}
      >
        <div style={cardStyle}>
          <div style={{ fontSize: 16, fontWeight: 900, marginBottom: 4 }}>
            Ranking por vendedor
          </div>
          <div style={{ fontSize: 12, color: "#64748b", marginBottom: 12 }}>
            Quem tem clientes em régua, atrasos e vendas recuperadas.
          </div>

          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr style={{ color: "#64748b", textAlign: "left" }}>
                  <th style={{ padding: 10 }}>Vendedor</th>
                  <th style={{ padding: 10 }}>Total</th>
                  <th style={{ padding: 10 }}>1/3</th>
                  <th style={{ padding: 10 }}>2/3</th>
                  <th style={{ padding: 10 }}>3/3</th>
                  <th style={{ padding: 10 }}>Hoje</th>
                  <th style={{ padding: 10 }}>Atrasados</th>
                  <th style={{ padding: 10 }}>Vendas rec.</th>
                  <th style={{ padding: 10 }}>Valor rec.</th>
                </tr>
              </thead>
              <tbody>
                {sellers.length === 0 ? (
                  <tr>
                    <td colSpan={9} style={{ padding: 14, color: "#64748b" }}>
                      Nenhuma régua de recuperação encontrada.
                    </td>
                  </tr>
                ) : (
                  sellers.map(seller => (
                    <tr key={seller.userId} style={{ borderTop: "1px solid #e5e7eb" }}>
                      <td style={{ padding: 10, fontWeight: 800 }}>{seller.name}</td>
                      <td style={{ padding: 10 }}>{seller.totalRecovery}</td>
                      <td style={{ padding: 10 }}>{seller.stageOne}</td>
                      <td style={{ padding: 10 }}>{seller.stageTwo}</td>
                      <td style={{ padding: 10 }}>{seller.stageThree}</td>
                      <td style={{ padding: 10, color: orange, fontWeight: 800 }}>
                        {seller.todayRecovery}
                      </td>
                      <td style={{ padding: 10, color: red, fontWeight: 900 }}>
                        {seller.overdueRecovery}
                      </td>
                      <td style={{ padding: 10, color: green, fontWeight: 900 }}>
                        {seller.recoveredSales}
                      </td>
                      <td style={{ padding: 10, color: green, fontWeight: 900 }}>
                        {money(seller.recoveredAmount)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div style={cardStyle}>
          <div style={{ fontSize: 16, fontWeight: 900, marginBottom: 4 }}>
            Últimas vendas recuperadas
          </div>
          <div style={{ fontSize: 12, color: "#64748b", marginBottom: 12 }}>
            Clientes que estavam na régua e depois viraram venda.
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 10, maxHeight: 360, overflowY: "auto" }}>
            {recoveredLatest.length === 0 ? (
              <div style={{ color: "#64748b", fontSize: 13 }}>
                Ainda não há venda recuperada registrada. Quando um cliente da régua comprar, aparecerá aqui.
              </div>
            ) : (
              recoveredLatest.map(item => (
                <div
                  key={item.id}
                  style={{
                    border: "1px solid #bbf7d0",
                    background: "#f0fdf4",
                    borderRadius: 12,
                    padding: 12
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                    <strong>{item.contactName || "Contato não identificado"}</strong>
                    <span style={{ color: green, fontWeight: 900 }}>
                      {money(item.valorVenda)}
                    </span>
                  </div>
                  <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>
                    Vendedor: {item.sellerName || "-"}
                  </div>
                  <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>
                    Venda em: {dateTime(item.updatedAt)}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      <div style={cardStyle}>
        <div style={{ fontSize: 16, fontWeight: 900, marginBottom: 4 }}>
          Últimos clientes em recuperação
        </div>
        <div style={{ fontSize: 12, color: "#64748b", marginBottom: 12 }}>
          Últimas réguas criadas no CRM.
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 10 }}>
          {latest.length === 0 ? (
            <div style={{ color: "#64748b", fontSize: 13 }}>
              Nenhum cliente em recuperação ainda.
            </div>
          ) : (
            latest.slice(0, 10).map(item => (
              <div
                key={item.id}
                style={{
                  border: "1px solid #fee2e2",
                  background: "#fff7f7",
                  borderRadius: 12,
                  padding: 12
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                  <strong>{item.contactName || "Contato não identificado"}</strong>
                  <span style={{ color: "#b91c1c", fontWeight: 900 }}>
                    {item.stage}
                  </span>
                </div>
                <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>
                  Vendedor: {item.sellerName || "-"}
                </div>
                <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>
                  Agendado: {dateTime(item.reminderDate || item.sendAt)}
                </div>
                <div style={{ fontSize: 12, marginTop: 8 }}>
                  {item.body}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default RecoveryDashboard;
